import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {DhiraaguUserTemplateColumnIds} from "@vesta/vos-types/modules/workspaceLevel/users/domain/DhiraaguUserTemplateColumnIds";
import type {KanbanBoardColumnConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {kanbanBoardColumnConfigValidator} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    type OttopilotDataViewerConfig,
    ottopilotDataViewerConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export type UsersTemplateSearchGroup = {
    userTemplateIds: Array<Uuid>;
    searchColumns: Array<string>;
};

export type UsersOttopilotDataViewerConfig = OttopilotDataViewerConfig & {
    templateSearchGroups?: Array<UsersTemplateSearchGroup>;
};

export type WorkspaceUsersConfiguration = {
    /**
     * Maps user template ID → semantic column IDs for workspaces that use
     * `parseUserTemplateColumnMap` (Dhiraagu, Emotorad, Bobcard-style configs).
     */
    userTemplateColumnMap?: Record<string, DhiraaguUserTemplateColumnIds>;
    usersPage?: {
        ottopilotDataViewerConfig?: UsersOttopilotDataViewerConfig;
        kanbanBoardPageView: {
            filterFieldId: string;
            columnConfig: Array<KanbanBoardColumnConfig>;
        };
    };
    userInvitationPage?: {
        ottopilotDataViewerConfig?: OttopilotDataViewerConfig;
        kanbanBoardPageView: {
            filterFieldId: string;
            columnConfig: Array<KanbanBoardColumnConfig>;
        };
    };
};

const dhiraaguUserTemplateColumnIdsValidator: zod.ZodType<DhiraaguUserTemplateColumnIds> = zod.strictObject({
    displayName: uuidZodValidator,
    dob: uuidZodValidator,
    gender: uuidZodValidator,
    staffCode: uuidZodValidator,
    verificationInvitedAt: uuidZodValidator,
    verificationInvitedBy: uuidZodValidator,
    verificationSubmittedOn: uuidZodValidator,
    verificationProcessedOn: uuidZodValidator,
    verificationProcessedBy: uuidZodValidator,
    verificationRejectReason: uuidZodValidator,
    organizationalUnitColumnId: uuidZodValidator.nullable(),
    profileImageColumnId: uuidZodValidator.nullable(),
});

const usersTemplateSearchGroupValidator: zod.ZodType<UsersTemplateSearchGroup> = zod.strictObject({
    userTemplateIds: uuidZodValidator.array(),
    searchColumns: zod.string().array(),
});

const usersOttopilotDataViewerConfigValidator: zod.ZodType<UsersOttopilotDataViewerConfig> = ottopilotDataViewerConfigValidator.extend({
    templateSearchGroups: usersTemplateSearchGroupValidator.array().optional(),
});

const usersPageConfigurationValidator = zod.strictObject({
    ottopilotDataViewerConfig: usersOttopilotDataViewerConfigValidator.optional(),
    kanbanBoardPageView: zod.strictObject({
        filterFieldId: zod.string(),
        columnConfig: kanbanBoardColumnConfigValidator.array(),
    }),
});

const userInvitationPageConfigurationValidator = zod.strictObject({
    ottopilotDataViewerConfig: ottopilotDataViewerConfigValidator.optional(),
    kanbanBoardPageView: zod.strictObject({
        filterFieldId: zod.string(),
        columnConfig: kanbanBoardColumnConfigValidator.array(),
    }),
});

export const workspaceUsersConfigurationValidator: zod.ZodType<WorkspaceUsersConfiguration> = zod.strictObject({
    userTemplateColumnMap: zod.record(zod.string(), dhiraaguUserTemplateColumnIdsValidator).optional(),
    usersPage: usersPageConfigurationValidator.optional(),
    userInvitationPage: userInvitationPageConfigurationValidator.optional(),
});
